import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  EnvelopeIcon,
  PhoneIcon,
  UserIcon,
  BriefcaseIcon,
  CurrencyEuroIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
  UserCircleIcon,
  IdentificationIcon,
  CalendarIcon,
  ClipboardDocumentIcon,
  XMarkIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { PlusIcon } from "@heroicons/react/24/solid";
import logo from "../../../assets/logo-nanthiat.png";
import ProspectForm from "./ProspectForm";

export default function ProspectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prospect, setProspect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [showAuditButton, setShowAuditButton] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showFinalModal, setShowFinalModal] = useState(false);
  const [interactions, setInteractions] = useState([]);
  const [prochainesActions, setProchainesActions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    typeInteraction: "",
    canal: "",
    date: "",
    contenu: "",
    prochaineEtape: "",
    dateProchaineInteraction: "",
    important: false,
  });

  const BACKEND_URL =
    window.location.hostname === "localhost"
      ? process.env.REACT_APP_BACKEND_URL_LOCAL + "/api"
      : process.env.REACT_APP_BACKEND_URL_PROD + "/api";

  useEffect(() => {
    const fetchProspect = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${BACKEND_URL}/prospects/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProspect(res.data);
      } catch (error) {
        console.error("Erreur lors du chargement du prospect :", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProspect();
  }, [id]);

  useEffect(() => {
    if (prospect?._id) {
      fetchInteractions();
      fetchNextSteps();
    }
  }, [prospect]);

  const fetchInteractions = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${BACKEND_URL}/interactions/${prospect._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sorted = res.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setInteractions(sorted);
    } catch (err) {
      console.error("Erreur récupération interactions :", err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
  
  const handleAddInteraction = async () => {
    const { typeInteraction, canal, date, contenu } = formData;
    if (!typeInteraction || !canal || !date || !contenu) {
      alert("Tous les champs obligatoires doivent être remplis.");
      return;
    }
  
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${BACKEND_URL}/interactions`,
        {
          ...formData,
          prospectId: prospect._id,
          referent: prospect.referent,
          statut: prospect.statut,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
  
      fetchInteractions();
      setShowForm(false);
      setFormData({
        typeInteraction: "",
        canal: "",
        date: "",
        contenu: "",
        prochaineEtape: "",
        dateProchaineInteraction: "",
        important: false,
      });
    } catch (err) {
      console.error("Erreur ajout interaction :", err);
    }
  };
  
  const handleDelete = async (id) => {
    const confirm = window.confirm("Supprimer cette interaction ?");
    if (!confirm) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${BACKEND_URL}/interactions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchInteractions();
    } catch (err) {
      console.error("Erreur suppression :", err);
    }
  };  

  const handleMarkAsDone = async (action) => {
    try {
      const token = localStorage.getItem("token");
  
      await axios.post(
        `${BACKEND_URL}/interactions`,
        {
          typeInteraction: action.type,
          canal: action.canal === "N/A" ? "Autre" : action.canal,
          date: new Date(),
          contenu: action.titre,
          statut: prospect.statut,
          referent: prospect.referent || "Non assigné",
          prospectId: prospect._id,
          commentaireInterne: "Interaction ajoutée depuis la section 'À Faire'.",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
  
      if (
        prospect.statut === "Prospect" &&
        action.titre === "Faire signer la Lettre d’Entrée en Relation"
      ) {
        await axios.put(
          `${BACKEND_URL}/prospects/${prospect._id}`,
          { statut: "Prospect Qualifié" },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        alert("Statut mis à jour vers 'Prospect Qualifié' !");
      }
  
      if (
        prospect.statut === "Prospect Qualifié" &&
        action.titre === "Fixer un rendez-vous pour l'audit patrimonial"
      ) {
        setShowAuditButton(true);
      }
  
      fetchNextSteps();
      fetchInteractions();
    } catch (err) {
      console.error("Erreur enregistrement interaction :", err);
    }
  };
  
  const handleConfirmAudit = async () => {
    try {
      const token = localStorage.getItem("token");
  
      const res = await axios.post(
        `${BACKEND_URL}/prospects/transformer/${prospect._id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
  
      // Redirection intelligente selon réponse
      if (res.data?.redirection === "societes") {
        navigate(`/societes/detail/${res.data.id}`);
      } else {
        navigate(`/familles/detail/${res.data.id}`);
      }
  
    } catch (error) {
      console.error("Erreur transformation en client :", error);
      alert("Erreur : " + error.response?.data?.message || "Erreur inconnue");
    }
  };  

  const fetchNextSteps = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${BACKEND_URL}/interactions/${prospect._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const allInteractions = res.data;

      const futures = allInteractions.filter(i => i.prochaineEtape || i.dateProchaineInteraction)
        .sort((a, b) => new Date(a.dateProchaineInteraction) - new Date(b.dateProchaineInteraction));

      const iconByType = {
        Appel: <PhoneIcon className="w-5 h-5 text-blue-500" />,
        Email: <EnvelopeIcon className="w-5 h-5 text-yellow-500" />,
        "Rendez-vous": <CalendarIcon className="w-5 h-5 text-green-500" />,
        "Message LinkedIn": <EnvelopeIcon className="w-5 h-5 text-indigo-500" />,
        "Note interne": <ClipboardDocumentIcon className="w-5 h-5 text-gray-500" />,
        Autre: <UserIcon className="w-5 h-5 text-gray-400" />,
      };

      const suggestionsParStatut = {
        Lead: [
          { type: "Appel", note: "Premier échange téléphonique" },
        ],
        Prospect: [
          { type: "Email", note: "Envoyer brochure de présentation" },
          { type: "Message LinkedIn", note: "Ajouter sur LinkedIn" },
          { type: "Rendez-vous", note: "Proposer un rendez-vous" },
          { type: "Note interne", note: "Préparer la Lettre d’Entrée en Relation" },
          { type: "Email", note: "Faire signer la Lettre d’Entrée en Relation" },
        ],
        "Prospect Qualifié": [
          { type: "Email", note: "Envoyer le questionnaire patrimonial" },
          { type: "Note interne", note: "Collecter documents financiers et administratifs" },
          { type: "Rendez-vous", note: "Fixer un rendez-vous pour l'audit patrimonial" },
        ],
        Client: [
          { type: "Note interne", note: "Lancer l’analyse du patrimoine" },
          { type: "Email", note: "Envoyer proposition d’allocation" },
          { type: "Appel", note: "Relancer pour retour sur proposition" },
        ],
      };

      const suggestionsDéjàRéalisées = new Set(
        allInteractions.map(i => `${i.typeInteraction}_${i.contenu?.trim()}`)
      );

      const suggestionsFiltrées = (suggestionsParStatut[prospect?.statut] || []).filter(
        sugg => !suggestionsDéjàRéalisées.has(`${sugg.type}_${sugg.note.trim()}`)
      );

      const actionsFutures = futures.map(item => ({
        icon: iconByType[item.typeInteraction],
        titre: item.prochaineEtape || "Étape à préciser",
        date: item.dateProchaineInteraction ? new Date(item.dateProchaineInteraction).toLocaleDateString() : null,
        canal: item.canal,
        type: item.typeInteraction,
      }));

      const actionsSuggestions = suggestionsFiltrées.map(sugg => ({
        icon: iconByType[sugg.type],
        titre: sugg.note,
        type: sugg.type,
        canal: "N/A",
        date: null,
      }));

      setProchainesActions([...actionsFutures, ...actionsSuggestions]);
    } catch (err) {
      console.error("Erreur récupération prochaines étapes :", err);
      setProchainesActions([]);
    }
  };

  if (loading || !prospect) {
    return <div className="text-center p-6 text-gray-600">Chargement du prospect...</div>;
  }

  const isPM = prospect.type === "Personne Morale";
  const initials = `${prospect.prenom?.[0] || ""}${prospect.nom?.[0] || ""}`.toUpperCase();

  return (
    <div className="bg-white rounded-lg shadow-md p-4 max-w-screen mx-auto my-2 space-y-4 h-[863px] relative">
        <XMarkIcon
            onClick={() => navigate(-1)}
            className="w-6 h-6 text-gray-500 hover:text-gray-700 cursor-pointer absolute top-2 right-4 z-10"
        />

      {/* 👤 Profil + Progression */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center h-[160px]">
        {/* 🟦 Bloc à diviser en 2 colonnes */}
        <div className="grid grid-cols-3 items-center w-full">
            {/* 🟠 Initiales + nom/statut (2/3) */}
            <div className="col-span-2 flex items-center space-x-6">
            <div className="w-[120px] h-[120px] flex items-center justify-center rounded-full bg-gray-200 text-5xl font-bold text-gray-700">
                {initials}
            </div>
            <div>
                <h2 className="text-4xl font-semibold text-gray-800">
                {prospect.prenom} {prospect.nom}
                </h2>
                <p className="text-base text-gray-500">{prospect.statut}</p>
            </div>
            </div>

            {/* 📅 Date ajoutée (1/3) */}
            <div className="col-span-1 flex flex-col items-start justify-center pl-20 text-gray-700">
                <div className="flex items-center space-x-2">
                    <CalendarIcon className="w-5 h-5 text-blue-500" />
                    <span className="text-lg font-medium">Ajouté le :</span>
                </div>
                <span className="text-md text-gray-600 ml-7">{new Date(prospect.dateAjout).toLocaleDateString()}</span>
            </div>
        </div>

        {/* ✅ Progress Bar améliorée */} 
        <div className="flex flex-col w-full">
        <div className="relative flex justify-between items-center w-full">
            {["Ajout du Prospect", "Lettre d’Entrée en Relation", "Audit Patrimonial"].map((step, index, arr) => {
            const isCompleted =
                (prospect.statut === "Prospect" && index === 0) ||
                (prospect.statut === "Prospect Qualifié" && index <= 1) ||
                (prospect.statut === "Client");

            const isCurrent =
                (prospect.statut === "Prospect" && index === 0) ||
                (prospect.statut === "Prospect Qualifié" && index === 1) ||
                (prospect.statut === "Client" && index === 2);

            return (
                <div key={index} className="relative flex-1 flex flex-col items-center">
                {/* Ligne de gauche */}
                {index !== 0 && (
                    <div className={`absolute top-4 -left-[150px] w-full h-1 z-0 
                    ${isCompleted ? "bg-blue-600" : "bg-gray-200"} 
                    `}></div>
                )}

                {/* Point */}
                <div className={`relative z-10 w-9 h-9 flex items-center justify-center rounded-full border-2 
                    ${isCurrent ? "bg-white border-blue-600 text-blue-600" :
                    isCompleted ? "bg-blue-600 border-blue-600 text-white" :
                    "bg-white border-gray-300 text-gray-400"}
                `}>
                    {isCompleted ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    ) : (
                    <span className="text-sm font-semibold">{index + 1}</span>
                    )}
                </div>

                {/* Label */}
                <span className="mt-2 text-xs text-center text-gray-700 w-[90px]">{step}</span>
                </div>
            );
            })}
        </div>
        </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-6 text-sm text-gray-700">
            {/* 🧾 Infos prospect */}
            <div className="md:col-span-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Carte d'information */}
            {[
                {
                icon: <UserIcon className="w-5 h-5 text-blue-600" />,
                label: "Nom",
                value: `${prospect.prenom} ${prospect.nom}`,
                },
                {
                icon: <PhoneIcon className="w-5 h-5 text-green-600" />,
                label: "Téléphone",
                value: prospect.telephone || "Non renseigné",
                },
                {
                icon: <EnvelopeIcon className="w-5 h-5 text-yellow-600" />,
                label: "Email",
                value: prospect.email || "Non renseigné",
                },
                {
                icon: <BriefcaseIcon className="w-5 h-5 text-gray-600" />,
                label: "Profession",
                value: prospect.profession || "Non renseignée",
                },
                {
                icon: <CurrencyEuroIcon className="w-5 h-5 text-indigo-600" />,
                label: "Enveloppe",
                value: `${prospect.enveloppe || "Non renseignée"} €`,
                },
                {
                icon: <IdentificationIcon className="w-5 h-5 text-purple-600" />,
                label: "Statut",
                value: prospect.statut,
                },
                {
                icon: <UserCircleIcon className="w-5 h-5 text-pink-600" />,
                label: "Référent",
                value: prospect.referent,
                },
                prospect.premierContact && {
                icon: <DocumentTextIcon className="w-5 h-5 text-blue-400" />,
                label: "1er contact",
                value: new Date(prospect.premierContact).toLocaleDateString(),
                },
            ]
                .filter(Boolean)
                .map((item, index) => (
                <div
                    key={index}
                    className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-md p-3 shadow-sm"
                >
                    {item.icon}
                    <div>
                    <p className="text-sm font-semibold text-gray-700">{item.label}</p>
                    <p className="text-sm text-gray-600">{item.value}</p>
                    </div>
                </div>
                ))}

            {/* Adresse */}
            <div className="md:col-span-2 bg-gray-50 border border-gray-200 rounded-md p-3 shadow-sm">
                <p className="text-sm font-semibold text-gray-700">Adresse</p>
                <p className="text-sm text-gray-600">
                {prospect.adresse}, {prospect.codePostal} {prospect.ville}
                </p>
            </div>

            {/* Infos Personne Morale */}
            {isPM && (
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-md p-3 shadow-sm">
                <BuildingOffice2Icon className="w-5 h-5 text-teal-600" />
                <div>
                    <p className="text-sm font-semibold text-gray-700">Statut Juridique</p>
                    <p className="text-sm text-gray-600">{prospect.statutJuridique}</p>
                </div>
                </div>
                <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-md p-3 shadow-sm">
                <BuildingOffice2Icon className="w-5 h-5 text-teal-600" />
                <div>
                    <p className="text-sm font-semibold text-gray-700">SIRET</p>
                    <p className="text-sm text-gray-600">{prospect.siret}</p>
                </div>
                </div>
                <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-md p-3 shadow-sm">
                <BuildingOffice2Icon className="w-5 h-5 text-teal-600" />
                <div>
                    <p className="text-sm font-semibold text-gray-700">Secteur</p>
                    <p className="text-sm text-gray-600">{prospect.secteurActivite}</p>
                </div>
                </div>
            </div>
            )}

            {/* Commentaire */}
            {prospect.commentaire && (
                <div className="md:col-span-2 bg-gray-50 border border-gray-200 rounded-md p-3 shadow-sm">
                <p className="text-sm font-semibold text-gray-700">Commentaire</p>
                <p className="text-sm text-gray-600">{prospect.commentaire}</p>
                </div>
            )}
            </div>


            {/* 💬 Interactions + À Faire (6 colonnes sur 12) */}
            <div className="md:col-span-6 space-y-4">
            {/* 💬 Interactions */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 h-fit min-h-[270px]">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">💬 Interactions</h3>
                    <button
                    onClick={() => setShowForm(true)}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition text-sm"
                    >
                    <PlusIcon className="w-4 h-4" />
                    Ajouter
                    </button>
                </div>

                {interactions.length > 0 ? (
                    <div className="space-y-3 pr-1 max-h-[200px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                    {interactions.map((item) => (
                        <div key={item._id} className="relative border-l-4 border-blue-500 pl-4 py-2 bg-white rounded-md shadow-sm">
                        <div className="flex justify-between">
                            <p className="text-sm text-gray-800 font-medium">
                            {item.typeInteraction} via {item.canal} – {new Date(item.date).toLocaleDateString()}
                            </p>
                            <button
                            onClick={() => handleDelete(item._id)}
                            className="text-red-600 hover:text-red-800"
                            >
                            <TrashIcon className="w-4 h-4" />
                            </button>
                        </div>
                        <p className="text-sm text-gray-600">{item.contenu}</p>

                        {item.prochaineEtape && (
                            <p className="text-sm text-blue-600 mt-1">
                            📌 {item.prochaineEtape}{" "}
                            {item.dateProchaineInteraction &&
                                `(le ${new Date(item.dateProchaineInteraction).toLocaleDateString()})`}
                            </p>
                        )}

                        {item.important && (
                            <div className="absolute top-1 right-10 text-yellow-500">
                            <ExclamationTriangleIcon className="w-5 h-5" title="Important" />
                            </div>
                        )}
                        </div>
                    ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500">Aucune interaction enregistrée.</p>
                )}
            </div>


            {/* ✅ À Faire */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 h-fit">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">À Faire</h3>

            {prochainesActions.length > 0 ? (
                <ul className="space-y-3">
                {prochainesActions.slice(0, 2).map((action, idx) => (
                    <li
                    key={idx}
                    className="bg-gray-100 border border-gray-300 rounded p-3 flex flex-col gap-2"
                    >
                    <div className="flex items-start gap-3">
                        <div className="mt-1">{action.icon}</div>
                        <div className="flex-1">
                        <p className="font-semibold text-gray-700">
                            {action.titre}{action.date && ` – ${action.date}`}
                        </p>
                        <p className="text-sm text-gray-500 italic">
                            ({action.type}{action.canal !== "N/A" && ` via ${action.canal}`})
                        </p>
                        </div>
                    </div>

                    <button
                        className="flex items-center gap-2 self-end text-sm text-green-600 hover:text-green-800"
                        onClick={() => handleMarkAsDone(action)}
                    >
                        <CheckIcon className="w-4 h-4" />
                        Marquer comme faite
                    </button>
                    </li>
                ))}
                </ul>
            ) : (
                <p className="text-sm text-gray-500">Aucune tâche prévue.</p>
            )}

            {showAuditButton && (
                <div className="mt-4 flex justify-center">
                <button
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                    onClick={() => setShowConfirmModal(true)}
                >
                    Audit Patrimonial
                </button>
                </div>
            )}

            {/* 💬 Modal de confirmation audit */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-xl w-[350px]">
                    <h3 className="text-lg font-semibold mb-4">Confirmer la transformation en client ?</h3>
                    <p className="text-sm text-gray-600 mb-6">
                    Cette action transférera les données dans les familles, mettra à jour le statut et supprimera le prospect.
                    </p>
                    <div className="flex justify-between">
                    <button
                        className="px-4 py-2 text-gray-600 hover:text-gray-800"
                        onClick={() => setShowConfirmModal(false)}
                    >
                        Annuler
                    </button>
                    <button
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                        onClick={handleConfirmAudit}
                    >
                        Confirmer
                    </button>
                    </div>
                </div>
                </div>
            )}

            {/* ✅ Modal final de succès */}
            {showFinalModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-xl w-[350px] text-center">
                    <h3 className="text-lg font-semibold mb-4">Transformation réussie ✅</h3>
                    <p className="text-sm text-gray-600 mb-6">
                    Le prospect a été converti en client. Vous pouvez désormais consulter sa fiche famille.
                    </p>
                    <button
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    onClick={() => window.location.href = "/prospects/familles"}
                    >
                    Aller à la fiche famille
                    </button>
                </div>
                </div>
            )}
            </div>
            </div>
        </div>

      {/* 🧭 Boutons d'action */}
      <div className="flex justify-end gap-4 pt-1">
        <button
          className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 transition"
          onClick={() => navigate(-1)}
        >
          Retour
        </button>
        <button
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
            onClick={() => setOpenEditModal(true)}
            >
            Modifier
        </button>
      </div>

      <ProspectForm
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        onSave={async (updatedData) => {
          try {
            const token = localStorage.getItem("token");
            const res = await axios.put(
              `http://localhost:3000/api/prospects/${updatedData._id}`,
              updatedData,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setProspect(res.data);
            setOpenEditModal(false);
          } catch (error) {
            console.error("Erreur lors de la mise à jour du prospect :", error);
          }
        }}
        prospect={prospect}
        referent={prospect.referent}
      />

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold">Nouvelle interaction</h3>

            <div className="grid grid-cols-1 gap-4">
                <div>
                <label className="block text-sm font-medium">Type d'interaction *</label>
                <select
                    name="typeInteraction"
                    value={formData.typeInteraction}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                >
                    <option value="">-- Sélectionner --</option>
                    {["Appel", "Email", "Rendez-vous", "Message LinkedIn", "Note interne", "Autre"].map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
                </div>

                <div>
                <label className="block text-sm font-medium">Canal *</label>
                <select
                    name="canal"
                    value={formData.canal}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                >
                    <option value="">-- Sélectionner --</option>
                    {["Téléphone", "Email", "LinkedIn", "Visio", "WhatsApp", "Autre"].map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
                </div>

                <div>
                <label className="block text-sm font-medium">Date *</label>
                <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                />
                </div>

                <div>
                <label className="block text-sm font-medium">Contenu *</label>
                <textarea
                    name="contenu"
                    value={formData.contenu}
                    onChange={handleChange}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md p-2"
                ></textarea>
                </div>

                <div>
                <label className="block text-sm font-medium">Prochaine étape</label>
                <input
                    name="prochaineEtape"
                    value={formData.prochaineEtape}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                />
                </div>

                <div>
                <label className="block text-sm font-medium">Date prochaine interaction</label>
                <input
                    type="date"
                    name="dateProchaineInteraction"
                    value={formData.dateProchaineInteraction}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                />
                </div>

                <div className="flex items-center gap-2">
                <input
                    type="checkbox"
                    name="important"
                    checked={formData.important}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600"
                />
                <label className="text-sm">Marquer comme important</label>
                </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
                <button
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg"
                onClick={() => setShowForm(false)}
                >
                Annuler
                </button>
                <button
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                onClick={handleAddInteraction}
                >
                Enregistrer
                </button>
            </div>
            </div>
        </div>
        )}
    </div>
  );
}